class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.clearPendingFocus();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    const inner = this.querySelector('.drawer__inner');
    const focusDrawer = () => {
      if (!this.isConnected || !this.classList.contains('active')) return;
      this.clearPendingFocus();
      const emptyContainer = this.classList.contains('is-empty') && this.querySelector('.drawer__inner-empty');
      const container = emptyContainer || this.querySelector('#CartDrawer');
      const focusElement = emptyContainer
        ? emptyContainer.querySelector('.drawer__close')
        : inner || this.querySelector('.drawer__close');
      if (container && focusElement) trapFocus(container, focusElement);
    };
    document.body.classList.add('overflow-hidden');

    // An already open drawer has no entrance transition after a section refresh.
    if (this.classList.contains('active') || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('animate', 'active');
      focusDrawer();
      return;
    }

    this.pendingFocusTransition = (event) => {
      // Child disclosures also animate. They must not move focus into the drawer.
      if (event.target !== inner || event.propertyName !== 'transform') return;
      focusDrawer();
    };
    this.addEventListener('transitionend', this.pendingFocusTransition);
    // Defer the class change so the closed transform can be painted first.
    this.openTimeout = setTimeout(() => {
      this.openTimeout = undefined;
      if (this.isConnected) this.classList.add('animate', 'active');
    });
  }

  clearPendingFocus() {
    clearTimeout(this.openTimeout);
    this.openTimeout = undefined;
    if (this.pendingFocusTransition) {
      this.removeEventListener('transitionend', this.pendingFocusTransition);
      this.pendingFocusTransition = null;
    }
  }

  close() {
    this.clearPendingFocus();
    this.clearNotice();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  clearNotice() {
    clearTimeout(this.noticeTimeout);
    clearTimeout(this.noticeHideTimeout);
    const notice = this.querySelector('[data-cart-notice]');
    if (notice) {
      notice.hidden = true;
      notice.textContent = '';
      notice.classList.remove('is-leaving');
    }
  }

  showNotice(message) {
    this.clearNotice();
    const notice = this.querySelector('[data-cart-notice]');
    if (!notice || !message) return;
    notice.textContent = message;
    notice.hidden = false;
    this.noticeTimeout = setTimeout(() => {
      notice.classList.add('is-leaving');
      this.noticeHideTimeout = setTimeout(() => this.clearNotice(), 300);
    }, 6000);
  }

  disconnectedCallback() {
    this.clearPendingFocus();
    this.clearNotice();
  }

  renderContents(parsedState, notice = '') {
    this.clearNotice();
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.showNotice(notice);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
